import { useCallback, useEffect, useMemo, useState } from "react";
import { Phone, Mail, MessageCircle, Download, Pencil, Search, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { waPhone } from "@/components/MarketingContacts";
import { TAX_CODE_RE } from "@/lib/jobForm";

const FILTERS = [["all", "Tutti"], ["manuale", "Inseriti manualmente"], ["web", "Acquisiti via web"]];
const SRC = { manuale: "Manuale", web: "Web" };
const FIELDS = [["first_name", "Nome"], ["last_name", "Cognome"], ["email", "Email"], ["phone", "Telefono"], ["address", "Indirizzo"], ["city", "Comune"], ["tax_code", "Codice fiscale / P. IVA (facoltativo)"]];

const toCsv = (rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = ["Nome", "Cognome", "Email", "Telefono", "Indirizzo", "Comune", "Codice fiscale / P.IVA", "Provenienza", "Richieste", "Ultimo appuntamento", "Consenso marketing"];
  const body = rows.map((r) => [r.first_name, r.last_name, r.email, r.phone, r.address, r.city, r.tax_code, SRC[r.source], r.jobs,
    r.last_date ? new Date(r.last_date).toLocaleDateString("it-IT") : "", r.marketing ? "Sì" : "No"].map(esc).join(";"));
  return "\ufeff" + [head.map(esc).join(";"), ...body].join("\n");
};

function EditClient({ c, onClose, onSaved }) {
  const [f, setF] = useState(c);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF(c); }, [c]);
  if (!f) return null;
  const save = async () => {
    const tax = (f.tax_code || "").trim().toUpperCase();
    if (tax && !TAX_CODE_RE.test(tax)) return toast.error("Codice fiscale (16 caratteri) o partita IVA (11 cifre) non valido");
    setBusy(true);
    try {
      const body = Object.fromEntries(FIELDS.map(([k]) => [k, (f[k] || "").trim()]));
      await api.put(`/admin/clients/${encodeURIComponent(c.email)}`, { ...body, tax_code: tax });
      toast.success("Dati cliente aggiornati su tutte le sue richieste");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!c} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="client-edit-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Modifica cliente</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {FIELDS.map(([k, label]) => (
            <div key={k} className={`space-y-1.5 ${k === "tax_code" || k === "address" ? "sm:col-span-2" : ""}`}><Label className="text-xs">{label}</Label>
              <Input value={f[k] || ""} maxLength={k === "tax_code" ? 16 : undefined} className={k === "tax_code" ? "font-mono uppercase" : ""}
                onChange={(e) => setF((p) => ({ ...p, [k]: k === "tax_code" ? e.target.value.toUpperCase().replace(/\s/g, "") : e.target.value }))} data-testid={`client-edit-${k}`} /></div>
          ))}
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} className="rounded-full" data-testid="client-edit-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ c, i, onEdit }) {
  return (
    <div className="grid gap-3 px-5 py-4 sm:grid-cols-[1fr_auto] sm:items-center" data-testid={`client-row-${i}`}>
      <div>
        <p className="font-semibold text-slate-900">{c.first_name} {c.last_name}
          <span className={`ml-2 rounded-full px-2 py-0.5 text-[11px] font-medium ${c.source === "web" ? "bg-sky-50 text-sky-700" : "bg-[#a2ca6b]/20 text-[#3d6b16]"}`} data-testid={`client-source-${i}`}>{SRC[c.source]}</span></p>
        <p className="mt-0.5 flex flex-wrap gap-x-4 text-sm text-slate-500">
          <span className="flex items-center gap-1" data-testid={`client-phone-${i}`}><Phone className="h-3.5 w-3.5" />{c.phone}
            {c.phone_dup_with?.length > 0 && <span className="font-bold text-amber-600" title={`Stesso numero di: ${c.phone_dup_with.join(", ")}`} data-testid={`client-phone-dup-${i}`}>*</span>}</span>
          <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{c.email}</span>
          <span>{[c.address, c.city].filter(Boolean).join(", ")}</span>
        </p>
        <p className="mt-0.5 text-xs text-slate-400">{c.phone_dup_with?.length > 0 && <span className="text-amber-700" data-testid={`client-phone-dup-note-${i}`}>* Stesso numero di: {c.phone_dup_with.join(", ")} · </span>}{c.tax_code ? <span className="font-mono text-slate-600">CF/P.IVA {c.tax_code} · </span> : ""}{c.jobs} richieste{c.last_date ? ` · ultimo appuntamento ${new Date(c.last_date).toLocaleDateString("it-IT")}` : ""}</p>
      </div>
      <div className="flex gap-2">
        <a href={`https://wa.me/${waPhone(c.phone)}`} target="_blank" rel="noreferrer" data-testid={`client-whatsapp-${i}`}>
          <Button size="sm" className="rounded-full bg-[#25D366] hover:bg-[#1ebe5a]"><MessageCircle className="h-4 w-4" /> WhatsApp</Button>
        </a>
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => onEdit(c)} data-testid={`client-edit-${i}`}><Pencil className="h-4 w-4" /> Modifica</Button>
      </div>
    </div>
  );
}

export const ClientsList = () => {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/clients").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (rows || []).filter((r) => (filter === "all" || r.source === filter)
      && (!s || [r.first_name, r.last_name, r.email, r.phone, r.city, r.tax_code].join(" ").toLowerCase().includes(s)));
  }, [rows, filter, q]);
  const count = (k) => (rows || []).filter((r) => k === "all" || r.source === k).length;
  const exportCsv = () => {
    const url = URL.createObjectURL(new Blob([toCsv(shown)], { type: "text/csv;charset=utf-8" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: `clienti-${filter}-${new Date().toISOString().slice(0, 10)}.csv` });
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {FILTERS.map(([k, label]) => (
          <Button key={k} size="sm" variant={filter === k ? "default" : "outline"} className="rounded-full" onClick={() => setFilter(k)} data-testid={`clients-filter-${k}`}>
            {label} <span className="ml-1 text-xs opacity-70">{count(k)}</span></Button>
        ))}
        <div className="relative ml-auto w-full sm:w-64"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome, telefono, CF…" className="pl-9" data-testid="clients-search" /></div>
        <Button size="sm" variant="outline" className="rounded-full" disabled={!shown.length} onClick={exportCsv} data-testid="clients-export-button"><Download className="h-4 w-4" /> Esporta Excel (CSV)</Button>
      </div>
      <div className="divide-y rounded-2xl border bg-white" data-testid="clients-list">
        {rows === null ? <p className="p-6 text-sm text-slate-500">Caricamento…</p>
          : shown.length === 0 ? <p className="p-6 text-center text-sm text-slate-500" data-testid="clients-empty">Nessun cliente.</p>
          : shown.map((c, i) => <Row key={c.email} c={c} i={i} onEdit={setEdit} />)}
      </div>
      <p className="mt-3 text-xs text-slate-500">Tutti i clienti con almeno una richiesta. "Manuale" = primo appuntamento inserito dall'ufficio, "Web" = prenotato dal sito. La modifica aggiorna i dati su tutte le sue richieste. <span className="font-bold text-amber-600">*</span> = numero di telefono condiviso con un altro cliente.</p>
      <EditClient c={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
};
